import { Component, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { roundTheTime30Min, getFormattedDate, checkIfDateExpired } from '@shared/utils';

export enum DateDirection {
  PREVIOUS,
  NEXT
}

export enum ChangeType {
  DATE,
  TIME
}

@Component({
  selector: 'app-ui-datetime-reminder',
  templateUrl: './ui-datetime-reminder.component.html',
  styleUrls: ['./ui-datetime-reminder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: UIDatetimeReminderComponent,
      multi: true
    }
  ],
  standalone: false
})
export class UIDatetimeReminderComponent implements ControlValueAccessor {
  date: Date | null = null;
  dateDirection = DateDirection;
  changeType = ChangeType;
  value: string | null;
  showPrevDateButton = false;
  showPrevTimeButton = false;
  ms = 1000 * 60 * 30;
  dateOptions: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'numeric', day: 'numeric' };

  languages = {
    fr: 'fr-CH',
    en: 'en-GB',
    de: 'de-CH'
  };

  @Input() currentLang: string;

  get day(): string {
    return this.date.toLocaleDateString(this.languages[this.currentLang] as string, this.dateOptions);
  }

  get time(): string {
    return `${this.date.getHours() < 10 ? '0' : ''}${this.date.getHours()}:${this.date.getMinutes() < 10 ? '0' : ''}${this.date.getMinutes()}`;
  }

  onChange = (value: string) => ({});
  onTouch = () => ({});

  registerOnChange(fn): void {
    this.onChange = fn;
    // if the formControlName value is null set current one
    if (this.value === null || this.value === '') {
      this.onChange(getFormattedDate(this.date));
    }
  }

  registerOnTouched(fn): void {
    this.onTouch = fn;
  }

  writeValue(value: string | null): void {
    this.value = value;

    this.date = this.generateDate(value);

    const { showPreviousDateButton, showPreviousTimeButton } = this.checkPreviousButtons(this.date);
    this.showPrevDateButton = showPreviousDateButton;
    this.showPrevTimeButton = showPreviousTimeButton;
  }

  onDateTimeChange(changeType: ChangeType, direction: DateDirection): void {
    const modifier: 1 | -1 = direction === this.dateDirection.NEXT ? 1 : -1;

    if (changeType === this.changeType.DATE) {
      this.date.setDate(this.date.getDate() + modifier * 1);
    } else {
      this.date.setTime(this.date.getTime() + modifier * this.ms);
    }

    this.date = checkIfDateExpired(this.date);

    const { showPreviousDateButton, showPreviousTimeButton } = this.checkPreviousButtons(this.date);
    this.showPrevDateButton = showPreviousDateButton;
    this.showPrevTimeButton = showPreviousTimeButton;

    this.onChange(getFormattedDate(this.date));
  }

  generateDate(value: string | null): Date {
    if (value === null || value === '') {
      return roundTheTime30Min(new Date());
    }

    const dateTimeArr = value.split('T');
    const dateArr = dateTimeArr[0].split('-');
    const timeArr = dateTimeArr[1].split(':');

    return roundTheTime30Min(new Date(Number(dateArr[0]), Number(dateArr[1]) - 1, Number(dateArr[2]), Number(timeArr[0]), Number(timeArr[1]), 0, 0));
  }

  checkPreviousButtons(date: Date): { showPreviousDateButton: boolean; showPreviousTimeButton: boolean } {
    const currentDateTime = roundTheTime30Min(new Date());
    const showPreviousDateButton = currentDateTime.getTime() < new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const showPreviousTimeButton =
      currentDateTime.getTime() < new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()).getTime();
    return { showPreviousDateButton, showPreviousTimeButton };
  }
}
